import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import useEmblaCarousel from 'embla-carousel-react'
import emblaCarouselClassNames from 'embla-carousel-class-names'
import emblaCarouselAutoHeight from 'embla-carousel-auto-height'
import classNames from 'classnames'

import useUserSubscriptions from 'hooks/useUserSubscriptions'
import useRouter from 'hooks/useRouter'

import { FEATURE_TYPES } from 'common/constants/subscription'

import { isOwner } from 'common/utils/permissions'

import { getShortDate } from 'utils/dateTime'

import { MODALS } from 'components/ModalManager/constants'
import Tooltip from 'components/Tooltip/Tooltip'

import useModalManager from 'hooks/useModalManager'
import useSizeDetector from 'hooks/useSizeDetector'

import List from './components/List/List'
import ListItem from './components/ListItem/ListItem'
import DangerMessage from './components/DangerMessage/DangerMessage'

import useLimits from './hooks/useLimits/useLimits'

import dangerIcon from './i/danger.svg'

import styles from './OrganizationLimits.module.scss'

const Tab = ({
    constraints,
    projectId = false,
    isOneTime = false,
    isMoreThenOneTab = false,
    productName,
    nextRenewalDate,
    onChangeHeight,
}) => {
    const { t } = useTranslation()
    const { historyPush } = useRouter()
    const { openModal } = useModalManager()

    const { ref: sizeDetectorRef, height } = useSizeDetector()

    useEffect(() => {
        onChangeHeight(height)
    }, [height])

    const { isHasDanger, isHasReached, dangerTypes, reachedTypes, limits } = useLimits(constraints)

    return (
        <li className={styles.tab} ref={sizeDetectorRef} style={{ margin: isMoreThenOneTab ? '0 10px' : 0 }}>
            {isMoreThenOneTab && (
                <div className={styles.tabName}>{isOneTime ? t('Single-Project Subscription') : t('Basic ')}</div>
            )}

            <ul className={styles.subscriptionInfo}>
                <li className={styles.subscriptionInfoItem}>
                    {t('Subscription')}: <span>{productName}</span> {/*{projectId}*/}
                </li>
                {nextRenewalDate && (
                    <li className={styles.subscriptionInfoItem}>
                        {t('Limits renewal')}: <span>{getShortDate(nextRenewalDate)}</span>
                        <Tooltip
                            iconSize={12}
                            tooltipText={t(
                                'On this date, the limits for views, leads, and premium templates will be refreshed for active subscription',
                            )}
                        />
                    </li>
                )}
            </ul>

            {isHasReached && (
                <DangerMessage>
                    {reachedTypes.length > 1 ? (
                        <Trans
                            i18nKey="You have reached the limits of your subscription. <lnk>Learn more</lnk>"
                            components={{
                                lnk: <a href="https://help.interacty.me/content/activity-and-limits" target="_blank" />,
                            }}
                        />
                    ) : (
                        <>
                            {reachedTypes[0] === FEATURE_TYPES.REVIEWS_COUNT && (
                                <Trans
                                    i18nKey="You have reached the limit of project views per month. <lnk>Learn more</lnk>"
                                    components={{
                                        lnk: <span onClick={() => openModal(MODALS.VIEWS_LIMITS_MODAL)} />,
                                    }}
                                />
                            )}
                            {reachedTypes[0] === FEATURE_TYPES.STORAGE_SIZE && (
                                <Trans
                                    i18nKey="Account storage is full. <lnk>Learn more</lnk>"
                                    components={{
                                        lnk: <span onClick={() => openModal(MODALS.STORAGE_LIMITS_MODAL)} />,
                                    }}
                                />
                            )}
                            {reachedTypes[0] === FEATURE_TYPES.PROJECTS_COUNT && (
                                <Trans
                                    i18nKey="You have reached the limit of projects. <lnk>Learn more</lnk>"
                                    components={{
                                        lnk: <span onClick={() => openModal(MODALS.PROJECTS_LIMITS_MODAL)} />,
                                    }}
                                />
                            )}
                            {reachedTypes[0] === FEATURE_TYPES.LEAD_CONTACTS && (
                                <Trans
                                    i18nKey="You have reached the limit of lead contacts. <lnk>Learn more</lnk>"
                                    components={{
                                        lnk: (
                                            <a
                                                href="https://help.interacty.me/en/content/activity-and-limits"
                                                target="_blank"
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </>
                    )}
                </DangerMessage>
            )}
            <List>
                {limits.map(item => (
                    <ListItem key={item.type} {...item} />
                ))}
            </List>
            {isOwner() && isHasDanger && !isOneTime && (
                <div className={styles.button}>
                    {dangerTypes.length > 1 ? (
                        <button
                            className="button"
                            data-variant="primary-light"
                            data-size="md"
                            onClick={() => historyPush('/billing-and-payments?tab=subscription')}
                        >
                            {t('Upgrade plan')}
                        </button>
                    ) : (
                        <>
                            {dangerTypes[0] === FEATURE_TYPES.REVIEWS_COUNT && (
                                <button
                                    className="button"
                                    data-variant="primary-light"
                                    data-size="md"
                                    onClick={() => historyPush('/billing-and-payments?tab=subscription')}
                                >
                                    {t('Get more views')}
                                </button>
                            )}
                            {dangerTypes[0] === FEATURE_TYPES.STORAGE_SIZE && (
                                <button
                                    className="button"
                                    data-variant="primary-light"
                                    data-size="md"
                                    onClick={() => historyPush('/billing-and-payments?tab=subscription')}
                                >
                                    {t('Increase storage')}
                                </button>
                            )}
                            {dangerTypes[0] === FEATURE_TYPES.PROJECTS_COUNT && (
                                <button
                                    className="button"
                                    data-variant="primary-light"
                                    data-size="md"
                                    onClick={() => historyPush('/billing-and-payments?tab=subscription')}
                                >
                                    {t('Get more projects')}
                                </button>
                            )}
                            {dangerTypes[0] === FEATURE_TYPES.LEAD_CONTACTS && (
                                <button
                                    className="button"
                                    data-variant="primary-light"
                                    data-size="md"
                                    onClick={() => historyPush('/billing-and-payments?tab=subscription')}
                                >
                                    {t('Get more lead contacts')}
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </li>
    )
}
const Tabs = ({ list }) => {
    const { t } = useTranslation()

    const [selectedTabIndex, setSelectedTabIndex] = useState(0)

    const { isHasDanger } = useLimits(list[selectedTabIndex]?.data.constraints)

    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            direction: 'ltr',
            axis: 'x',
            align: 'start',
            startIndex: 0,
            speed: 10,
            loop: false,
            slidesToScroll: 1,
            dragFree: false,
            active: list.length > 1,
            skipSnaps: true,
            containScroll: '',
            inViewThreshold: 0,
        },
        [
            emblaCarouselClassNames({
                selected: styles.isSelected,
                draggable: styles.isDraggable,
                dragging: styles.isDragging,
            }),
            emblaCarouselAutoHeight(),
        ],
    )

    const scrollTo = useCallback(index => emblaApi && emblaApi.scrollTo(index), [emblaApi])
    const onSelect = useCallback(() => {
        if (!emblaApi) return
        setSelectedTabIndex(emblaApi.selectedScrollSnap())
    }, [emblaApi, setSelectedTabIndex])
    useEffect(() => {
        if (!emblaApi) return
        onSelect()
        emblaApi.on('select', onSelect)
    }, [emblaApi, onSelect])

    const onChangeTabHeight = () => {
        if (!emblaApi) return
        emblaApi.reInit()
    }

    return (
        <div className={styles.tabs}>
            <h4 className={styles.title}>
                {t('Activity')}
                <div className={classNames(styles.titleIcon, { [styles.isVisibleTitleIcon]: isHasDanger })}>
                    <img src={dangerIcon} alt="danger" />
                </div>
            </h4>
            <div className={styles.slider} ref={emblaRef}>
                <ul className={styles.list}>
                    {list.map((item, index) => (
                        <Tab
                            key={index}
                            isOneTime={item.isOneTime}
                            projectId={item.data.projectId}
                            productName={item.data.productName}
                            nextRenewalDate={item.data.nextRenewalDate}
                            constraints={item.data.constraints}
                            isMoreThenOneTab={list.length > 1}
                            onChangeHeight={onChangeTabHeight}
                        />
                    ))}
                </ul>
            </div>
            {list.length > 1 && (
                <ul className={styles.navigation}>
                    {list.map((item, index) => (
                        <li
                            className={classNames(styles.navigationItem, {
                                [styles.isActiveNavigationItem]: index === selectedTabIndex,
                            })}
                            onClick={() => scrollTo(index)}
                        >
                            <span className={styles.navigationItemDot} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

const OrganizationLimits = () => {
    const { basicConstraints, oneProjectConstraintsArray } = useUserSubscriptions()

    return (
        <Tabs
            list={[
                ...oneProjectConstraintsArray.map(item => ({ data: item, isOneTime: true })),
                { data: basicConstraints, isOneTime: false },
            ]}
        />
    )
}

export default OrganizationLimits
